import { ReactElement, useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { lineXYLength } from '@geometry'
import WallEdit, { WallEditChange } from '@editor/components/WallEdit'
import { coordinateEventName } from '@ui/events'
import { useControlStore } from '@editorStores'
import { useHideElements, useSceneElement } from '@editorHooks'
import useWallUpdate from '../../hooks/useWallUpdate'
import { buildFormWall, Coordinates, WallFormValues } from '../../utils'
import WallFormSceneElement from '../WallFormSceneElement'
import WallFormSceneMarker from '../WallFormSceneMarker'

interface Props {
  /** the saved wall, or the drawn draft */
  wall: ShapeObject
  isDraft: boolean
  /** show the editable 2D line (`WallEdit`) for moving and extending the wall in the scene */
  sceneEditEnabled: boolean
  /**
   * called with the form values after every move/extension in the scene; when it saves them and the
   * save fails, the scene edit is dropped again
   */
  onSceneEdit?: (values: WallFormValues) => Promise<unknown> | void
}

const isValidCoordinates = (point: Coordinates) =>
  Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z)

/**
 * Renders the wall's form state into the scene in place of the saved wall: the saved wall and the
 * connected walls moved along by "Intelligentes Verschieben" are hidden and drawn from the form
 * (`WallFormSceneElement`), and in 2D `WallEdit` shows the wall as an editable line whose moves and
 * extensions are written straight into the form. The model store only ever holds saved data.
 */
const WallFormScene = ({ wall, isDraft, sceneEditEnabled, onSceneEdit }: Props): ReactElement => {
  const {
    setValue,
    getValues,
    reset,
    formState: { defaultValues },
  } = useFormContext<WallFormValues>()

  // watch the single coordinates: the fields write `end.x` etc., and the `start`/`end` objects
  // react-hook-form returns keep their identity across such writes, so memos would go stale
  const [startX, startY, startZ, endX, endY, endZ] = useWatch({
    name: ['start.x', 'start.y', 'start.z', 'end.x', 'end.y', 'end.z'],
  }) as number[]
  const placement = useWatch({ name: 'placement' }) as WallPlacement
  const dependentWalls = useWatch({ name: 'dependentWalls' }) as ShapeObject[]

  const formWall = useMemo(() => {
    const start = { x: startX, y: startY, z: startZ }
    const end = { x: endX, y: endY, z: endZ }

    // a cleared field has no value yet: keep showing the last complete geometry
    if (!isValidCoordinates(start) || !isValidCoordinates(end)) return wall

    return buildFormWall(wall, start, end, placement)
  }, [wall, startX, startY, startZ, endX, endY, endZ, placement])

  // a draft isn't part of the model, so there is no saved wall to hide
  const hiddenWallGuids = useMemo(
    () => [...(isDraft ? [] : [wall.guid]), ...dependentWalls.map(({ guid }) => guid)],
    [isDraft, wall.guid, dependentWalls],
  )

  useHideElements(hiddenWallGuids)

  const formWalls = useMemo(() => [formWall, ...dependentWalls], [formWall, dependentWalls])

  const sceneElement = useMemo(
    () => ({
      enabled: true,
      properties: { walls: formWalls },
      component: WallFormSceneElement,
    }),
    [formWalls],
  )

  useSceneElement(sceneElement)

  const update = useWallUpdate(wall, isDraft)

  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  // while a point is picked for a coordinate field, clicks must reach the scene, not the wall line
  const isCoordinateInputMode = !!selectionModeKey?.startsWith(coordinateEventName)

  const handleSceneEdit = useCallback(
    async ({ start, end, dependentWalls }: WallEditChange) => {
      const options = { shouldDirty: true }
      setValue('start', { x: start.x, y: start.y, z: start.z }, options)
      setValue('end', { x: end.x, y: end.y, z: end.z }, options)
      setValue('dependentWalls', dependentWalls, options)

      // keep "Länge ändern" in line with the length dragged in the scene
      if (defaultValues?.start && defaultValues?.end) {
        const originalLength = lineXYLength(
          defaultValues.start as Coordinates,
          defaultValues.end as Coordinates,
        )
        setValue('lengthAdjustment', lineXYLength(start, end) - originalLength)
      }

      try {
        await onSceneEdit?.(getValues())
      } catch {
        // the save reports the error; show the saved walls again instead of the unsaved edit
        reset()
      }
    },
    [setValue, getValues, reset, defaultValues, onSceneEdit],
  )

  const wallEdit = useMemo(
    () => ({
      enabled: sceneEditEnabled && !isCoordinateInputMode,
      properties: { wall: formWall, dependentWalls, isDraft, update, onChange: handleSceneEdit },
      component: WallEdit,
    }),
    [
      sceneEditEnabled,
      isCoordinateInputMode,
      formWall,
      dependentWalls,
      isDraft,
      update,
      handleSceneEdit,
    ],
  )

  useSceneElement(wallEdit)

  return <WallFormSceneMarker wall={formWall} enabled={!sceneEditEnabled} />
}

export default WallFormScene
